// compareValues — value coercion + comparison for the blueprint operator nodes
// (Comparison Operators: == != > < >= <=; Logical Operators: && || !).
//
// Pins carry bool / number (int or float) / string — whatever a literal,
// variable, or event pin yields. `valuesEqual` is type-aware; `compareOrder`
// gives an ordering for > < >= <=; `toBoolean` coerces any pin to a bool for
// the logical operators. All three are pure + unit-tested
// (tests/unit/compareValues.test.mjs). No imports.
//
// ── valuesEqual (== / !=) ──
// Same-type values compare with `===`, so an int and an equal float compare
// equal (both are JS numbers), and strings/bools compare exactly. Cross-type
// comparisons coerce ONLY when both sides clearly denote one kind: a boolean vs
// a bool-like value (true/false, 1/0, "true"/"false") compares as booleans, and
// two numeric values (a number or a numeric string) compare as numbers. The
// boolean handling exists because there is no "Make Literal Bool" node —
// comparing an event's `isLast` bool against a constant means wiring a literal
// int/string, and that must still work. Anything else falls back to a string
// comparison. Unwired pins arrive as null (see _resolveDataPin): two unwired
// pins are equal; a wired-vs-unwired pin is not.

const isNully = (v) => v === null || v === undefined

function asNumber(v) {
  if (typeof v === 'number') return Number.isNaN(v) ? null : v
  if (typeof v === 'string') {
    const s = v.trim()
    if (s === '') return null
    const n = Number(s)
    return Number.isNaN(n) ? null : n
  }
  return null
}

function asBool(v) {
  if (typeof v === 'boolean') return v
  if (typeof v === 'number') return v === 1 ? true : v === 0 ? false : null
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase()
    if (s === 'true'  || s === '1') return true
    if (s === 'false' || s === '0') return false
  }
  return null
}

/**
 * True when `a` equals `b` under the pin-comparison rules above.
 * @param {boolean|number|string|null|undefined} a
 * @param {boolean|number|string|null|undefined} b
 * @returns {boolean}
 */
export function valuesEqual(a, b) {
  if (isNully(a) || isNully(b)) return isNully(a) && isNully(b)

  // Same JS type: number==number (int/float), string==string, boolean==boolean.
  if (typeof a === typeof b) return a === b

  // A boolean on either side → compare as booleans when the other is bool-like.
  if (typeof a === 'boolean' || typeof b === 'boolean') {
    const thisBool  = typeof a === 'boolean' ? a : b
    const otherBool = asBool(typeof a === 'boolean' ? b : a)
    return otherBool === null ? false : thisBool === otherBool
  }

  // Otherwise, if both look numeric, compare as numbers (2 vs "2", 2 vs "2.0").
  const na = asNumber(a), nb = asNumber(b)
  if (na !== null && nb !== null) return na === nb

  // Last resort: string comparison.
  return String(a) === String(b)
}

// Number, numeric string, or bool → a number for ordering; else null.
function toComparableNumber(v) {
  if (typeof v === 'boolean') return v ? 1 : 0
  return asNumber(v)
}

/**
 * Ordering comparison for > < >= <=. Returns -1 (a<b), 0 (a==b), or 1 (a>b).
 * When both sides coerce to a number (number, numeric string, or bool) the
 * compare is numeric; otherwise it is a lexicographic string compare. Unwired
 * pins (null) coerce to "" on the string path.
 * @returns {-1|0|1}
 */
export function compareOrder(a, b) {
  const na = toComparableNumber(a)
  const nb = toComparableNumber(b)
  if (na !== null && nb !== null) {
    return na < nb ? -1 : na > nb ? 1 : 0
  }
  const sa = String(a ?? '')
  const sb = String(b ?? '')
  return sa < sb ? -1 : sa > sb ? 1 : 0
}

/**
 * Coerce any pin value to a boolean for the logical operators (&& || !). Real
 * booleans pass through; numbers are false only at 0/NaN; strings are false
 * only when empty or "false"/"0" (so a "false" string doesn't read as truthy);
 * null/undefined/other are false.
 * @returns {boolean}
 */
export function toBoolean(v) {
  if (typeof v === 'boolean') return v
  if (typeof v === 'number') return v !== 0 && !Number.isNaN(v)
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase()
    return !(s === '' || s === 'false' || s === '0')
  }
  return false
}
